import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useLingui } from '@lingui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  SlotCoverageStatus,
  type SlotChildPresence,
  type SlotCoverageGroup,
  type SlotCoverageSlot,
  type SlotCoverageStatusType,
} from '@/types/coverage';
import {
  OutsideBookingIcon,
  PresenceSourceBadge,
  useOutsideBookingReason,
  useDriftReason,
} from './ChildPresenceRow';

interface GroupStaffingStripProps {
  group: SlotCoverageGroup;
  axisStart: string;
  axisEnd: string;
  dayStartHour: number;
  dayEndHour: number;
}

/** A cell narrower than this shows no child count. */
const MIN_COUNT_LABEL_WIDTH_PX = 28;

/** The tooltip lists at most this many children, then "+N more". */
const MAX_TOOLTIP_CHILDREN = 8;

const HATCH =
  'repeating-linear-gradient(45deg, rgb(148 163 184 / 0.8) 0 2px, transparent 2px 5px)';
const DOTS = 'radial-gradient(rgb(148 163 184) 1px, transparent 1px)';

const toMinutes = (hhmm: string): number => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

const formatWeighted = (value: number): string =>
  String(Math.round(value * 10) / 10);

/** The children present in a slot: arrival < slot end AND departure > slot start. */
const childrenInSlot = (
  children: SlotChildPresence[],
  slot: SlotCoverageSlot
): SlotChildPresence[] => {
  const start = toMinutes(slot.start);
  const end = toMinutes(slot.end);
  return children.filter(
    (child) => toMinutes(child.arrival) < end && toMinutes(child.departure) > start
  );
};

function statusAppearance(status: SlotCoverageStatusType): {
  className: string;
  style?: CSSProperties;
} {
  switch (status) {
    case SlotCoverageStatus.SURPLUS:
      return {
        className: 'bg-muted text-muted-foreground',
        style: { backgroundImage: HATCH },
      };
    case SlotCoverageStatus.SUFFICIENT:
      return { className: 'bg-success/80 text-success-foreground' };
    case SlotCoverageStatus.WARNING:
      return { className: 'bg-warning text-warning-foreground' };
    case SlotCoverageStatus.CRITICAL:
      return { className: 'bg-destructive text-destructive-foreground' };
    case SlotCoverageStatus.IN_UNIT:
      return { className: 'bg-info/60 text-foreground' };
    case SlotCoverageStatus.NOT_CONFIGURED:
      return {
        className: 'bg-transparent text-foreground',
        style: { backgroundImage: DOTS, backgroundSize: '4px 4px' },
      };
    case SlotCoverageStatus.NO_CHILDREN:
    default:
      return { className: 'bg-transparent text-foreground' };
  }
}

/**
 * Legend of the strip colours, shown under the By Group day view (#2038 UX pass). The
 * statuses are the ones the strip paints; NO_CHILDREN is blank and needs no entry.
 */
export function StaffingLegend({ className }: { className?: string }) {
  const { i18n } = useLingui();
  const entries: { status: SlotCoverageStatusType; label: string }[] = [
    {
      status: SlotCoverageStatus.SUFFICIENT,
      label: i18n._({ id: 'shifts.slotCoverage.legend.sufficient', message: 'Enough staff' }),
    },
    {
      status: SlotCoverageStatus.WARNING,
      label: i18n._({ id: 'shifts.slotCoverage.legend.warning', message: 'Tight' }),
    },
    {
      status: SlotCoverageStatus.CRITICAL,
      label: i18n._({ id: 'shifts.slotCoverage.legend.critical', message: 'Too few staff' }),
    },
    {
      status: SlotCoverageStatus.SURPLUS,
      label: i18n._({ id: 'shifts.slotCoverage.legend.surplus', message: 'More staff than needed' }),
    },
    {
      status: SlotCoverageStatus.IN_UNIT,
      label: i18n._({ id: 'shifts.slotCoverage.status.inUnit', message: 'Combined with other groups' }),
    },
    {
      status: SlotCoverageStatus.NOT_CONFIGURED,
      label: i18n._({ id: 'shifts.slotCoverage.status.notConfigured', message: 'Staffing model not configured' }),
    },
  ];
  return (
    <ul
      className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}
      aria-label={i18n._({ id: 'shifts.slotCoverage.legend.label', message: 'Staffing per quarter hour' })}
      data-testid="staffing-legend"
    >
      <li className="font-medium">
        {i18n._({ id: 'shifts.slotCoverage.legend.title', message: 'Strip under each group:' })}
      </li>
      {entries.map((entry) => {
        const appearance = statusAppearance(entry.status);
        return (
          <li key={entry.status} className="inline-flex items-center gap-1.5">
            <span
              className={cn('inline-block h-3 w-5 rounded-sm border', appearance.className)}
              style={appearance.style}
              aria-hidden="true"
            />
            {entry.label}
          </li>
        );
      })}
    </ul>
  );
}

/** Tracks an element's rendered width. */
function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    setWidth(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

/**
 * A 12px strip at the bottom of a group's day timeline: one cell per 15-minute
 * slot, colored by staffing status and aligned with the hour grid and shift bars.
 * Every cell carries the numbers in its aria-label and tooltip, so color is never
 * the only signal.
 */
export function GroupStaffingStrip({
  group,
  axisStart,
  axisEnd,
  dayStartHour,
  dayEndHour,
}: GroupStaffingStripProps) {
  const { i18n } = useLingui();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const outsideBookingReason = useOutsideBookingReason();
  const driftReason = useDriftReason();

  const dayStart = dayStartHour * 60;
  const dayEnd = dayEndHour * 60;
  const totalMinutes = dayEnd - dayStart;

  const statusLabel = (status: SlotCoverageStatusType): string => {
    switch (status) {
      case SlotCoverageStatus.SURPLUS:
        return i18n._({ id: 'shifts.slotCoverage.status.surplus', message: 'Surplus staff' });
      case SlotCoverageStatus.SUFFICIENT:
        return i18n._({ id: 'shifts.slotCoverage.status.sufficient', message: 'Sufficient' });
      case SlotCoverageStatus.WARNING:
        return i18n._({ id: 'shifts.slotCoverage.status.warning', message: 'Warning' });
      case SlotCoverageStatus.CRITICAL:
        return i18n._({ id: 'shifts.slotCoverage.status.critical', message: 'Critical' });
      case SlotCoverageStatus.IN_UNIT:
        return i18n._({ id: 'shifts.slotCoverage.status.inUnit', message: 'Combined with other groups' });
      case SlotCoverageStatus.NOT_CONFIGURED:
        return i18n._({ id: 'shifts.slotCoverage.status.notConfigured', message: 'Staffing model not configured' });
      case SlotCoverageStatus.NO_CHILDREN:
      default:
        return i18n._({ id: 'shifts.slotCoverage.status.noChildren', message: 'No children' });
    }
  };

  const describeSlot = (slot: SlotCoverageSlot): string =>
    i18n._({
      id: 'shifts.slotCoverage.cell.ariaLabel',
      message:
        '{time}, {status}: {children} children ({weighted} weighted), required staff {required}, scheduled staff {scheduled}, qualified staff {qualified}',
      values: {
        time: `${slot.start}-${slot.end}`,
        status: statusLabel(slot.status),
        children: slot.children,
        weighted: formatWeighted(slot.weighted_children),
        required: slot.required_staff,
        scheduled: slot.scheduled_staff,
        qualified: slot.scheduled_qualified_staff,
      },
    });

  return (
    <TooltipProvider delayDuration={100}>
      <div
        ref={ref}
        role="group"
        aria-label={i18n._({
          id: 'shifts.slotCoverage.strip.ariaLabel',
          message: 'Staffing of {group}, {start}-{end}',
          values: { group: group.group_name, start: axisStart, end: axisEnd },
        })}
        data-testid="group-staffing-strip"
        className="absolute inset-x-0 bottom-0 h-3 overflow-hidden rounded-b-md"
      >
        {group.closed ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted text-xs leading-none text-muted-foreground">
            {i18n._({ id: 'shifts.slotCoverage.closed', message: 'Closed' })}
          </div>
        ) : (
          group.slots.map((slot) => {
            const start = Math.max(toMinutes(slot.start), dayStart);
            const end = Math.min(toMinutes(slot.end), dayEnd);
            if (end <= start) return null;

            const leftPercent = ((start - dayStart) / totalMinutes) * 100;
            const widthPercent = ((end - start) / totalMinutes) * 100;
            const showCount =
              slot.children > 0 &&
              (width * widthPercent) / 100 >= MIN_COUNT_LABEL_WIDTH_PX;
            const appearance = statusAppearance(slot.status);

            return (
              <Tooltip key={slot.start}>
                <TooltipTrigger asChild>
                  <div
                    role="img"
                    aria-label={describeSlot(slot)}
                    data-status={slot.status}
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                      'absolute inset-y-0 flex items-center justify-center border-r border-background/60 text-xs leading-none',
                      appearance.className
                    )}
                    style={{
                      ...appearance.style,
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`,
                    }}
                  >
                    {showCount ? slot.children : null}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1 text-sm">
                    <div className="font-semibold">
                      {slot.start}-{slot.end} · {statusLabel(slot.status)}
                    </div>
                    <div>
                      {i18n._({
                        id: 'shifts.slotCoverage.tooltip.children',
                        message: 'Children: {children} ({weighted} weighted)',
                        values: {
                          children: slot.children,
                          weighted: formatWeighted(slot.weighted_children),
                        },
                      })}
                    </div>
                    {slot.status !== SlotCoverageStatus.NOT_CONFIGURED && (
                      <div>
                        {slot.min_staff_floor_applied
                          ? i18n._({
                              id: 'shifts.slotCoverage.tooltip.requiredFloor',
                              message: 'Required staff: {required} (minimum applies)',
                              values: { required: slot.required_staff },
                            })
                          : i18n._({
                              id: 'shifts.slotCoverage.tooltip.required',
                              message: 'Required staff: {required}',
                              values: { required: slot.required_staff },
                            })}
                      </div>
                    )}
                    <div>
                      {i18n._({
                        id: 'shifts.slotCoverage.tooltip.scheduled',
                        message: 'Scheduled staff: {scheduled}',
                        values: { scheduled: slot.scheduled_staff },
                      })}
                    </div>
                    <div>
                      {i18n._({
                        id: 'shifts.slotCoverage.tooltip.qualified',
                        message: 'Qualified staff: {qualified}',
                        values: { qualified: slot.scheduled_qualified_staff },
                      })}
                    </div>
                    {(() => {
                      const present = childrenInSlot(group.children, slot);
                      if (present.length === 0) return null;
                      const hidden = present.length - MAX_TOOLTIP_CHILDREN;
                      return (
                        <ul className="mt-1 space-y-0.5 border-t pt-1" data-testid="slot-children">
                          {present.slice(0, MAX_TOOLTIP_CHILDREN).map((child) => {
                            const reason = outsideBookingReason(child);
                            const drift = driftReason(child);
                            return (
                              <li key={child.child_id} className="flex items-center gap-1 text-xs">
                                <span className="max-w-32 truncate">{child.display_name}</span>
                                {reason && <OutsideBookingIcon reason={reason} />}
                                {drift && <OutsideBookingIcon reason={drift} />}
                                <span className="ml-auto tabular-nums">
                                  {child.arrival}-{child.departure}
                                </span>
                                <PresenceSourceBadge source={child.arrival_source} />
                                {String(child.departure_source) !== String(child.arrival_source) && (
                                  <PresenceSourceBadge source={child.departure_source} />
                                )}
                              </li>
                            );
                          })}
                          {hidden > 0 && (
                            <li className="text-xs text-muted-foreground">
                              {i18n._({
                                id: 'shifts.slotCoverage.tooltip.moreChildren',
                                message: '+{count} more',
                                values: { count: hidden },
                              })}
                            </li>
                          )}
                        </ul>
                      );
                    })()}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })
        )}
      </div>
    </TooltipProvider>
  );
}
